import type { SceneLogEntry, StoryPack } from '@plotjack/core';
import { trackLabel } from '@plotjack/core';

interface Props {
  entry: SceneLogEntry;
  story: StoryPack;
  next?: { title: string; previously?: string };
  onContinue: () => void;
}

const PLAN: Record<SceneLogEntry['plan'], string> = {
  blocked: "Blocked. The narrator's plan never happened, so it cost no Dread.",
  delayed: "Delayed. The plan limped in a beat late and cost no Dread.",
  bent: 'Bent off course by your cards, so it cost no Dread.',
  stands: 'It went ahead with one change. Dread +1 from the plan.',
  untouched: 'It went ahead exactly as written, unopposed. Dread +2 from the plan.',
};

/** Short wrap-up between "Outcome" and "Next scene". */
export function SceneSummary({ entry, story, next, onContinue }: Props) {
  return (
    <section className="panel scene-summary" aria-labelledby="scene-summary-title">
      <p className="eyebrow">Scene complete</p>
      <h2 id="scene-summary-title">{entry.title}</h2>
      <p className="scene-summary__story">{entry.summary}</p>
      <div className="scene-summary__grid">
        <div>
          <h3>Your cards</h3>
          {entry.plays.length ? (
            <ul>
              {entry.plays.map((p, i) => (
                <li key={i}>
                  <strong>{p.cardName}</strong> on {p.targetLabel}: {p.text}
                </li>
              ))}
            </ul>
          ) : (
            <p>None played.</p>
          )}
        </div>
        <div>
          <h3>Narrator's plan</h3>
          <p>{PLAN[entry.plan]}</p>
          <h3>Meters</h3>
          <ul className="scene-summary__meters">
            <li className={entry.dreadDelta > 0 ? 'wc-line--bad' : ''}>Dread {entry.dreadDelta > 0 ? '+' : ''}{entry.dreadDelta}</li>
            {(['hope', 'chaos', 'fate'] as const).map((t) => (
              <li key={t}>
                {trackLabel(story, t)} {entry.trackDeltas[t] ? `${entry.trackDeltas[t]! > 0 ? '+' : ''}${entry.trackDeltas[t]}` : '+0'}
              </li>
            ))}
          </ul>
        </div>
      </div>
      {next && (
        <p className="scene-summary__next">
          <span className="quest-label">Next</span> <strong>{next.title}</strong>
          {next.previously ? `. ${next.previously}` : ''}
        </p>
      )}
      <button type="button" className="btn btn-primary" onClick={onContinue} autoFocus>
        Continue to deck choice
      </button>
    </section>
  );
}
